/**
 * Cache function result by the first passed object.
 * Useful to efficiently compute the same representations of a set of objects.
 * Uses weakmap to avoid unnecessary memory usage.
 */
export function weakPerObjectCache<P extends any[], R extends any>(
  fn: (...args: P) => R
): (...args: P) => R {
  const cache = new WeakMap();

  return (...args: P) => {
    let currentMap = cache;
    // recursively traverse the weak-map to get the cached value
    // it is cached by first N "type=object"
    for (const a of args) {
      if (!currentMap.has(a)) break;
      const next = currentMap.get(a);
      if (next instanceof WeakMap) currentMap = next;
      else return next as R;
    }

    currentMap = cache;
    // in case of cache miss
    // build a recursive structure
    for (const a of args.slice(0, -1)) {
      if (typeof a !== 'object') break;

      if (!currentMap.has(a)) {
        const next = new WeakMap();
        currentMap.set(a, next);
        currentMap = next;
      } else {
        currentMap = currentMap.get(a);
      }
    }

    const res = fn(...args);
    currentMap.set(args[args.length - 1], res);
    return res as R;
  };
}

/**
 * Cache function result by the passed arguments.
 * Useful to memoize compute functions.
 * Uses weakmap to avoid unnecessary memory usage.
 */
export function weakCache<P extends any[], R extends any>(
  fn: (...args: P) => R
): (...args: P) => R {
  const cache = new Map();

  return (...args: P) => {
    let currentMap = cache;
    // recursively traverse the weak-map to get the cached value
    // it is cached by first N "type=object"
    for (const a of args) {
      if (!currentMap.has(a)) break;
      const next = currentMap.get(a);
      if (next instanceof Map) currentMap = next;
      else return next as R;
    }

    currentMap = cache;
    // in case of cache miss
    // build a recursive structure
    for (const a of args.slice(0, -1)) {
      if (!currentMap.has(a)) {
        const next = new Map();
        currentMap.set(a, next);
        currentMap = next;
      } else {
        currentMap = currentMap.get(a);
      }
    }

    const res = fn(...args);
    currentMap.set(args[args.length - 1], res);
    return res as R;
  };
}

export function arraySequentialDifference<T>(
  supersetArray: T[],
  subsetArray: T[]
): T[] {
  const result = [];
  let subInd = 0;

  for (let supInd = 0; supInd < supersetArray.length; supInd++) {
    if (
      subInd < subsetArray.length &&
      supersetArray[supInd] === subsetArray[subInd]
    )
      subInd++;
    else result.push(supersetArray[supInd]);
  }

  return result;
}

export function anyApplies<T>(arr: T[], f: (t: T) => boolean): boolean {
  for (const item of arr) {
    if (f(item)) return true;
  }
  return false;
}

export function allApply<T>(arr: T[], f: (t: T) => boolean): boolean {
  for (const item of arr) {
    if (!f(item)) return false;
  }
  return true;
}

export function getIdGenerator(start = 0) {
  let id = start;
  return () => ++id;
}

// export const uniformPipe = <T>(initial: T, ...functions: ((t: T) => T)[]) =>
//   functions.reduce((acc, f) => f(acc), initial);

export const chain = <T>(initial: T) => new Chainable<T>(initial);

class Chainable<T> {
  constructor(private val: T) {}

  /**
   * Apply a function to the argument.
   * @param fn Function changing the value
   */
  public apply<R>(fn: (val: T) => R) {
    this.val = fn(this.val) as unknown as T;
    return this as unknown as Chainable<R>;
  }

  public get() {
    return this.val;
  }
}

export const countBy = <T, K extends string | number | symbol>(
  arr: T[],
  key: (i: T) => K | undefined,
  initial?: Record<K, number>
) =>
  arr.reduce((groups, item) => {
    const k = key(item);
    if (k === undefined) return groups;

    groups[k] = (groups[k] ?? 0) + 1;
    return groups;
  }, initial ?? ({} as Record<K, number>));

export const groupBy = <T, K extends string | number | symbol>(
  arr: T[],
  key: (i: T) => K | undefined,
  initial?: Record<K, T[]>
) =>
  arr.reduce((groups, item) => {
    const k = key(item);
    if (k === undefined) return groups;

    groups[k] = groups[k] ?? [];
    groups[k].push(item);
    return groups;
  }, initial ?? ({} as Record<K, T[]>));

export const groupByToArr = <T>(
  arr: T[],
  key: (i: T) => number | undefined,
  initial?: T[][]
) =>
  arr.reduce((groups, item) => {
    const k = key(item);
    if (k === undefined) return groups;

    groups[k] = groups[k] ?? [];
    groups[k].push(item);
    return groups;
  }, initial ?? ([] as T[][]));

export const removeUndefined = <T>(...arr: (T | undefined)[]) =>
  arr.filter(a => a !== undefined) as T[];

export const foldsLeft = <I, O>(
  arr: I[],
  fn: (acc: O, cur: I, arr: I[]) => O,
  initial: O,
  dropFirst = false,
  dropLast = true
) => {
  const result = [];
  let prev = initial;

  if (!dropFirst) result.push(prev);

  for (const cur of arr) {
    prev = fn(prev, cur, arr);
    result.push(prev);
  }
  if (dropLast && arr.length) result.pop();

  return result;
};
